import 'core-js/modules/es.symbol.js';
import 'core-js/modules/es.symbol.description.js';
import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.array.every.js';
import 'core-js/modules/es.array.join.js';
import 'core-js/modules/es.array.map.js';
import 'core-js/modules/es.object.assign.js';
import 'core-js/modules/es.object.to-string.js';
import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useRef, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { AppSwitcherIcon, SearchIcon } from '@cognite/cogs-icons';
import { useBoolean, getThemeClassNames, useCombinedRef, MAX_TABLET_WIDTH, useOnKeypress } from '@cognite/cogs-utils';
import '../../tokens/color-tokens/index.js';
import '../../tokens/elevation-tokens/index.js';
import { Heading, Body } from '../../tokens/typography/typography.js';
import { Button } from '../button/button.js';
import { Dropdown } from '../dropdown/dropdown.js';
import { Flex } from '../flex/flex.js';
import { Input } from '../input/input.js';
import { Menu } from '../menu/menu.js';
import { ProductLogo } from '../product-logo/product-logo.js';
import { TopbarContext } from './top-bar-context.js';
import { isKeyPressed, getKeyLabel } from './utils.js';

var TopbarLeft = function TopbarLeft(props) {
  return jsx("div", Object.assign({}, props, {
    className: classNames('cogs-v10', 'cogs-topbar__left', props.className)
  }));
};
TopbarLeft.displayName = 'Topbar.Left';
var TopbarRight = function TopbarRight(props) {
  return jsx("div", Object.assign({}, props, {
    className: classNames('cogs-v10', 'cogs-topbar__right', props.className)
  }));
};
TopbarRight.displayName = 'Topbar.Right';
var TopbarLogo = function TopbarLogo(_a) {
  var children = _a.children,
    type = _a.type,
    _a$size = _a.size,
    size = _a$size === undefined ? '32px' : _a$size,
    rest = __rest(_a, ["children", "type", "size"]);
  var className = classNames('cogs-v10', 'cogs-topbar__logo', rest.className);
  return jsxs(Flex, Object.assign({
    alignItems: "center",
    gap: 10
  }, rest, {
    className: className,
    children: [jsx(ProductLogo, {
      size: size,
      type: type
    }), children && jsx(Heading, {
      className: "cogs-v10 cogs-topbar__logo-title",
      level: 6,
      children: children
    })]
  }));
};
TopbarLogo.displayName = 'Topbar.Logo';
var AppSwitcher = function AppSwitcher(_a) {
  var children = _a.children,
    rest = __rest(_a, ["children"]);
  var _useBoolean = useBoolean(false),
    value = _useBoolean.value,
    toggle = _useBoolean.toggle,
    setFalse = _useBoolean.setFalse;
  return jsx(Dropdown, Object.assign({
    onClickOutside: setFalse,
    visible: value,
    content: jsxs(Menu, {
      inverted: false,
      style: {
        width: 288
      },
      children: [jsx(Menu.Header, {
        children: "Applications"
      }), children]
    })
  }, rest, {
    children: jsx(TopbarButton, {
      onClick: toggle,
      toggled: value,
      type: "ghost",
      "aria-label": "Switch Apps",
      icon: jsx(AppSwitcherIcon, {})
    })
  }));
};
AppSwitcher.displayName = 'AppSwitcher';
var colorMapper = {
  InField: '--cogs-decorative--blue--100',
  Maintain: '--cogs-decorative--pink--100',
  InRobot: '--cogs-decorative--yellow--200',
  Discover: '--cogs-decorative--purple--100',
  CDF: '--cogs-decorative--grayscale--1000'
};
var AppSwitcherItem = function AppSwitcherItem(_a) {
  var icon = _a.icon,
    onClick = _a.onClick,
    description = _a.description,
    children = _a.children,
    rest = __rest(_a, ["icon", "onClick", "description", "children"]);
  return jsx(Menu.Item, Object.assign({
    onClick: onClick
  }, rest, {
    children: jsxs(Flex, {
      gap: 10,
      children: [jsx(Flex, {
        justifyContent: "center",
        alignItems: "center",
        style: {
          backgroundColor: "var(" + colorMapper[icon] + ")"
        },
        className: "cogs-v10 cogs-topbar__appswitcher-item",
        children: jsx(ProductLogo, {
          inverted: icon === 'CDF',
          size: "24px",
          type: icon
        })
      }), jsxs(Flex, {
        direction: "column",
        children: [jsx(Heading, {
          level: 6,
          children: children
        }), jsx(Body, {
          size: "medium",
          children: description
        })]
      })]
    })
  }));
};
AppSwitcherItem.displayName = 'AppSwitcher.Item';
var TopbarButton = function TopbarButton(props) {
  return jsx(Button, Object.assign({}, props, {
    className: "cogs-v10 cogs-topbar__button",
    type: "ghost"
  }));
};
TopbarButton.displayName = 'Topbar.Button';
var TopbarLink = function TopbarLink(_a) {
  var toggled = _a.toggled,
    children = _a.children,
    inverted = _a.inverted,
    asContainerOnly = _a.asContainerOnly,
    props = __rest(_a, ["toggled", "children", "inverted", "asContainerOnly"]);
  if (asContainerOnly) return jsx("span", {
    className: classNames.apply(undefined, ['cogs-v10', 'cogs-button', "cogs-button--type-ghost"].concat(getThemeClassNames(inverted), [toggled && 'cogs-button--toggled', 'cogs-topbar__link--as-container-only'])),
    children: children
  });
  return jsx("a", Object.assign({
    rel: "noopener noreferrer"
  }, props, {
    className: classNames.apply(undefined, ['cogs-v10', 'cogs-button', "cogs-button--type-ghost"].concat(getThemeClassNames(inverted), [toggled && 'cogs-button--toggled', 'cogs-topbar__link'])),
    children: children
  }));
};
TopbarLink.displayName = 'Topbar.Link';
var TopbarSearch = /*#__PURE__*/forwardRef(function (_a, ref) {
  var _a$keysToExpand = _a.keysToExpand,
    keysToExpand = _a$keysToExpand === undefined ? ['/'] : _a$keysToExpand,
    _a$keysToCollapse = _a.keysToCollapse,
    keysToCollapse = _a$keysToCollapse === undefined ? ['Escape'] : _a$keysToCollapse,
    _a$cancelSearchText = _a.cancelSearchText,
    cancelSearchText = _a$cancelSearchText === undefined ? 'Cancel' : _a$cancelSearchText,
    onFocus = _a.onFocus,
    onBlur = _a.onBlur,
    rest = __rest(_a, ["keysToExpand", "keysToCollapse", "cancelSearchText", "onFocus", "onBlur"]);
  var innerRef = useRef();
  var combinedRef = useCombinedRef(innerRef, ref);
  var _useContext = useContext(TopbarContext),
    topbarWidth = _useContext.width;
  var isNotDesktopView = useMemo(function () {
    return Boolean(topbarWidth && topbarWidth <= MAX_TABLET_WIDTH);
  }, [topbarWidth]);
  var _useState = useState(false),
    isExpanded = _useState[0],
    setIsExpanded = _useState[1];
  useEffect(function () {
    var _a, _b;
    if (isExpanded) {
      (_a = innerRef.current) === null || _a === undefined ? undefined : _a.focus();
    } else {
      (_b = innerRef.current) === null || _b === undefined ? undefined : _b.blur();
    }
  }, [isExpanded]);
  var handleKeyPress = useCallback(function (event) {
    if (keysToExpand.length > 0 && keysToExpand.every(function (key) {
      return isKeyPressed(event, key);
    }) && !isExpanded) {
      setIsExpanded(true);
      event.preventDefault();
    } else if (keysToCollapse.length > 0 && keysToCollapse.every(function (key) {
      return isKeyPressed(event, key);
    }) && isExpanded) {
      setIsExpanded(false);
      event.preventDefault();
    }
  }, [keysToExpand, keysToCollapse, isExpanded]);
  useOnKeypress(handleKeyPress, document.body);
  var handleOnFocus = useCallback(function (event) {
    if (onFocus) {
      onFocus(event);
    }
    // if we are in mobile view, we don't want to expand the search on focus
    if (!isNotDesktopView) {
      setIsExpanded(true);
    }
  }, [onFocus, isNotDesktopView]);
  var handleOnBlur = useCallback(function (event) {
    if (onBlur) {
      onBlur(event);
    }
    // if we are not in mobile view, we don't want to collapse the search on blur
    // except when the search input is empty
    if (!isNotDesktopView && !event.target.value) {
      setIsExpanded(false);
    }
  }, [onBlur, isNotDesktopView]);
  if (isNotDesktopView && !isExpanded) {
    return jsx(Button, {
      className: "cogs-v10 cogs-topbarexp__search--icon-button",
      "aria-label": "topbar-search",
      icon: jsx(SearchIcon, {}),
      onClick: function onClick() {
        setIsExpanded(true);
      }
    });
  }
  var tabletViewStyle = isNotDesktopView && isExpanded ? {
    position: 'absolute',
    right: '0px',
    left: '0px',
    padding: '12px',
    backgroundColor: 'var(--cogs-themed-surface--muted)',
    display: 'grid',
    gap: '8px',
    alignItems: 'center',
    width: 'fill-available',
    gridTemplateColumns: '1fr min-content'
  } : {};
  var getSuffix = function getSuffix() {
    if (isNotDesktopView) {
      return '';
    }
    if (isExpanded) {
      return keysToCollapse.map(function (key) {
        return getKeyLabel(key);
      }).join('+');
    }
    return keysToExpand.map(function (key) {
      return getKeyLabel(key);
    }).join('+');
  };
  return jsxs("div", {
    className: classNames('cogs-v10', "cogs-topbar__search" + (isExpanded ? '--expanded' : '')),
    style: tabletViewStyle,
    children: [jsx(Input, Object.assign({}, rest, {
      onClick: function onClick(e) {
        var _a;
        setIsExpanded(true);
        (_a = rest.onClick) === null || _a === undefined ? undefined : _a.call(rest, e);
      },
      ref: combinedRef,
      onFocus: handleOnFocus,
      onBlur: handleOnBlur,
      icon: jsx(SearchIcon, {}),
      suffix: getSuffix(),
      variant: "solid"
    })), isNotDesktopView && isExpanded && jsx(Button, {
      className: "cogs-v10 cogs-topbar__search--cancel-button",
      type: "ghost",
      onClick: function onClick() {
        setIsExpanded(false);
      },
      children: cancelSearchText
    })]
  });
});
TopbarSearch.displayName = 'Topbar.Search';

export { AppSwitcher, AppSwitcherItem, TopbarButton, TopbarLeft, TopbarLink, TopbarLogo, TopbarRight, TopbarSearch };
